import React, { useState } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import _ from 'lodash';
import {
  COURSE_TYPE,
  COURSE_TYPE_SHORT_NAMES_MAPPING,
  PLAN_TYPES,
  PLAN_TYPE_ACCORDING_TO_COURSE_TYPE,
} from '../../../constants';
import { getSubPlanTypes } from '../../../services/apiService';

const validationErrorClassNames = 'text-xs -mt-2';
const inputClass = 'mb-3 outline-none px-3 py-2 text-sm rounded-md w-full';
const labelClass = 'text-sm font-medium mb-1';

export default function CreateCouponDialog({
  onCreateButtonClicked,
  handleCloseButton,
  open,
}) {
  const [formData, setFormData] = useState({
    couponCode: '',
    discountAmount: '',
    goalType: COURSE_TYPE.DATA_SCIENCE,
    planType: PLAN_TYPES.PRO_LIVE,
    subPlanType: '',
    expiryDate: '',
    owner: '',
    validForUsers: '',
    beforeSignupDate: '',
  });
  const [validationError, setValidationError] = useState({});
  const [subPlanOptions, setSubPlanOptions] = useState([]);
  React.useEffect(async () => {
    if (
      formData?.planType === PLAN_TYPES.MASTERS ||
      formData?.goalType === COURSE_TYPE.UG_DIPLOMA_CS
    ) {
      setFormData((prev) => {
        return { ...prev, subPlanType: '' };
      });
      return;
    }
    try {
      const apiData = {
        courseType: formData?.goalType,
        planType: 'pro_live',
      };
      const response = await getSubPlanTypes(apiData);
      if (response.length) {
        const mappedArray = response.map((singleSubPlanType) => {
          return {
            name: singleSubPlanType?.subPlanName,
            value: singleSubPlanType?.subPlanType,
          };
        });
        setFormData((prev) => {
          return { ...prev, subPlanType: mappedArray[0]?.value };
        });
        setSubPlanOptions(mappedArray);
      }
    } catch (err) {
      console.error(err);
    }
  }, [formData?.goalType, formData?.planType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const validateForm = (formData) => {
    setValidationError({});
    const vError = {};
    const mandatoryFields = ['couponCode', 'discountAmount', 'expiryDate'];
    for (const field of mandatoryFields) {
      if (!formData[field]) vError[field] = 'Mandatory Attribute';
    }
    if (
      formData.discountAmount &&
      !Number.isInteger(Number(formData.discountAmount))
    ) {
      vError.discountAmount = 'Amount should be in integer';
    }

    setValidationError(vError);
    return vError;
  };

  const handleCreateButton = () => {
    const validationError = validateForm(formData);
    if (!_.isEmpty(validationError)) return;
    const couponToBeCreated = {
      ...formData,
      expiryDate: moment(formData?.expiryDate?.trim()).toISOString(),
      beforeSignupDate: moment(
        formData?.beforeSignupDate?.trim()
      ).toISOString(),
      discountAmount: Number(formData.discountAmount),
      subPlanType: formData?.subPlanType,
      validForUsers: formData?.validForUsers?.trim()
        ? formData.validForUsers
            ?.trim()
            ?.split(',')
            ?.map((u) => u.trim())
        : undefined,
      owner: formData?.owner?.trim() ? formData?.owner?.trim() : undefined,
    };
    onCreateButtonClicked(couponToBeCreated);
  };

  return (
    <Dialog
      onClose={handleCloseButton}
      aria-labelledby='simple-dialog-title'
      open={open}
      maxWidth='sm'
      fullWidth
      PaperProps={{ style: { minWidth: 520 } }}
    >
      <DialogTitle id='simple-dialog-title'>Create New Coupon</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter details to create a new coupon.
        </DialogContentText>

        <form className='flex flex-col gap-1 mt-2'>
          <>
            <label htmlFor='couponCode' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Coupon Code</label>
            <input
              className={inputClass}
              style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
              id='couponCode'
              name='couponCode'
              type='text'
              value={formData.couponCode}
              onChange={handleChange}
              onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
            />
            {validationError.couponCode && (
              <div className={validationErrorClassNames} style={{ color: '#EF4444' }}>
                {validationError.couponCode}
              </div>
            )}
            <label htmlFor='discountAmount' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Discount Amount (in Paise)</label>
            <input
              className={inputClass}
              style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
              id='discountAmount'
              name='discountAmount'
              type='number'
              value={formData.discountAmount}
              onChange={handleChange}
              onWheel={(e) => e.target.blur()}
            />
            {validationError.discountAmount && (
              <div className={validationErrorClassNames} style={{ color: '#EF4444' }}>
                {validationError.discountAmount}
              </div>
            )}
            <label htmlFor='goalType' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Goal Type</label>
            <select
              className={inputClass}
              style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
              id='goalType'
              name='goalType'
              value={formData.goalType}
              onChange={handleChange}
            >
              {Object.values(COURSE_TYPE).map((cType) => (
                <option key={cType} value={cType}>
                  {COURSE_TYPE_SHORT_NAMES_MAPPING[cType]}
                </option>
              ))}
            </select>
            <label htmlFor='planType' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Plan Type</label>
            <select
              className={inputClass}
              style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
              id='planType'
              name='planType'
              value={formData.planType}
              onChange={handleChange}
            >
              {PLAN_TYPE_ACCORDING_TO_COURSE_TYPE[formData.goalType].map(
                (cType) => (
                  <option key={cType} value={cType}>
                    {cType}
                  </option>
                )
              )}
            </select>
            {formData?.planType !== PLAN_TYPES.MASTERS &&
              formData?.goalType !== COURSE_TYPE.UG_DIPLOMA_CS && (
                <>
                  <label htmlFor='subPlanType' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Sub Plan Type</label>
                  <select
                    className={inputClass}
                    style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
                    id='subPlanType'
                    name='subPlanType'
                    value={formData.subPlanType}
                    onChange={handleChange}
                  >
                    {subPlanOptions?.map((cType, index) => (
                      <option key={index} value={cType?.value}>
                        {cType?.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

            <label htmlFor='validForUsers' className={labelClass} style={{ color: 'var(--text-secondary)' }}>
              Valid For Users <span style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>(comma separated)</span>
            </label>
            <input
              className={inputClass}
              style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
              id='validForUsers'
              name='validForUsers'
              type='text'
              value={formData.validForUsers}
              onChange={handleChange}
            />
          </>

          <label htmlFor='expiryDate' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Expiry Date</label>
          <input
            className={inputClass}
            style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
            id='expiryDate'
            name='expiryDate'
            type='datetime-local'
            value={formData.expiryDate}
            onChange={handleChange}
          />
          {validationError.expiryDate && (
            <div className={validationErrorClassNames} style={{ color: '#EF4444' }}>
              {validationError.expiryDate}
            </div>
          )}
          <label htmlFor='beforeSignupDate' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Signed Up Before</label>
          <input
            className={inputClass}
            style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
            id='beforeSignupDate'
            name='beforeSignupDate'
            type='date'
            value={formData.beforeSignupDate}
            onChange={handleChange}
          />
          <label htmlFor='owner' className={labelClass} style={{ color: 'var(--text-secondary)' }}>Owner</label>
          <input
            className={inputClass}
            style={{ backgroundColor: 'var(--input-background)', border: '1px solid var(--input-border)', color: 'var(--input-text)' }}
            id='owner'
            name='owner'
            type='text'
            value={formData.owner}
            onChange={handleChange}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseButton} color='primary'>
          Cancel
        </Button>
        <Button onClick={handleCreateButton} color='primary'>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
